import { ApiResponse } from "@/utils/ApiResponse";
import type { QueryKey } from "@tanstack/vue-query";
import { useQueryClient, useMutation } from "@tanstack/vue-query";
import { useToast } from "vue-toastification";
import { ProductoInterface } from "../../interfaces/productoInterfaces/Producto.interface";
import { ToggleVisibilityProductoAction } from "../../actions/productoActions";

const toast = useToast();

type ToggleCtx = {
  previousEntries: [QueryKey, unknown][];
};

export function useProductoToggleVisib() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (productoId: string) =>
      ToggleVisibilityProductoAction(productoId),

    onMutate: async (productoId: string): Promise<ToggleCtx> => {
      await queryClient.cancelQueries({ queryKey: ["ProductosList"] });
      const previousEntries = queryClient.getQueriesData({
        queryKey: ["ProductosList"],
      });

      queryClient.setQueriesData<ApiResponse<ProductoInterface>>(
        { queryKey: ["ProductosList"] },
        (old) => {
          if (!old?.data?.data?.productos) return old;
          return {
            ...old,
            data: {
              ...old.data,
              data: {
                ...old.data.data,
                productos: old.data.data.productos.map((producto) =>
                  producto.id === productoId
                    ? {
                        ...producto,
                        isVisible: producto.isVisible === 1 ? 0 : 1,
                      }
                    : producto
                ),
              },
            },
          };
        }
      );

      return { previousEntries };
    },

    onError: (_error, _productoId, context) => {
      context?.previousEntries?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
      toast.error("No se pudo actualizar la visibilidad del producto.");
    },

    onSuccess: () => {
      toast.success("Visibilidad del producto actualizada.");
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["ProductosList"] });
    },
  });

  return {
    ...mutation,
  };
}
